<template>
  <GenericDialogLayout
    :data-qa="didMount ? 'did-mount' : ''"
    :loading="isSubmitting"
    :header-text="$t('booking.actions.new')"
    :submit-button-text="$t('booking.action.general.book_booking')"
    :submit-button-class="'vcita-medium-button'"
    :cancel-button-text="$t('booking.actions.cancel')"
    :is-submit-btn-disabled="shouldSubmitButtonBeDisabled"
    @submit-dialog="handleSubmit"
    @close-dialog="handleClose"
    @loader-time-out="handleLoaderTimeOut"
    :displayRightPane="true"
    class="booking-dialog-container"

  >
    <template v-slot:left-side-pane>
      <v-form class="dialog-form" ref="appointmentForm">
        <v-layout class="left-side-container" column>

          <v-layout class="booking-details-section" column>
            <v-layout class="input-section-title service-input-title" justify-start align-center>
              {{$t('booking.info.service')}}
            </v-layout>

            <ServicesListSelect
              class="service-select"
              data-qa="service-select-input"
              :categories-data="modifiedCategories"
              :rules="serviceRules"
              v-model="selectedService"
              @change="deleteCopy"
            />
            <v-layout v-if="!$vuetify.breakpoint.mdAndUp" class="dialog-separator service-separator"></v-layout>
            <v-layout class="input-section-title" justify-space-between align-center>
              <span class="appointment-details-title">{{ $t('appointment.info.appointment_details') }} </span>
              <RecurrenceSelector @formatted-input="val => formattedRecurrenceParams = val"
                                  :start_time="startTimeDateObject" v-model="recurrenceParams"
                                  :duration="getDuration()"
                                  v-if="$vuetify.breakpoint.mdAndUp" class="recurring-link"/>
            </v-layout>


            <v-layout
              class="input-section"
              column
            >
              <vcita-text-field
                :rules="customTitleRules"
                class="custom-title-input"
                v-if="isSelectedServiceCustom"
                maxlength="80"
                :label="$t('appointment.info.subject')"
                v-model="customTitle"
              />
              <v-layout :column="isAllDay || !$vuetify.breakpoint.mdAndUp">
                <v-layout :class="{'spacing-gap': isAllDay, 'margin-right': !isAllDay && $vuetify.breakpoint.mdAndUp, 'service-separator': !isAllDay && !$vuetify.breakpoint.mdAndUp}">
                  <DatePicker
                    @toggle="handleAllDay"
                    v-model="startDate"
                    :switch-on="isAllDay"
                    :label="$t('booking.info.start_date')"
                    all-day-enable
                    :class="{'booking-date-picker': !isAllDay, 'one-flex-grow': isAllDay}"
                    data-qa="service-date-input"
                    :rules="dateRules"
                  >
                  </DatePicker>
                  <v-layout class="one-flex-grow" v-if="isAllDay">
                    <HourPicker
                      name="start_time"
                      :rules="startTimeRules"
                      :label="$t('booking.info.start_at')"
                      data-qa="service-start-time-input"
                      :error-messages="serverErrors.start_time || []"
                      :amPmMode="amPmMode"
                      v-model="startTime"
                    >
                    </HourPicker>
                  </v-layout>
                </v-layout>
                <!--All day state-->
                <v-layout class="spacing-gap" v-if="isAllDay">
                  <DatePicker
                    v-model="endDate"
                    :label="$t('booking.info.end_date')"
                    class="one-flex-grow"
                    data-qa="service-date-input"
                    :not-before="startDate"
                    :rules="dateRules"
                  >
                  </DatePicker>
                  <v-layout class="one-flex-grow">
                    <HourPicker
                      data-qa="service-end-time-input"
                      :error-messages="serverErrors.end_time || []"
                      :label="$t('booking.info.end_at')"
                      name="end_time"
                      :rules="endTimeRules"
                      :amPmMode="amPmMode"
                      v-model="endTime"
                    >
                    </HourPicker>
                  </v-layout>
                </v-layout>
                <!--Regular state-->
                <v-layout class="hour-pickers" v-if="!isAllDay">
                  <HourPicker
                    name="start_time"
                    :rules="startTimeRules"
                    :label="$t('booking.info.start_at')"
                    data-qa="service-start-time-input"
                    class="booking-start-time"
                    :error-messages="serverErrors.start_time || []"
                    :amPmMode="amPmMode"
                    v-model="startTime"
                    prepend-inner-icon="icon-Clock"
                  >
                  </HourPicker>
                  <HourPicker
                    data-qa="service-end-time-input"
                    :error-messages="serverErrors.end_time || []"
                    :label="$t('booking.info.end_at')"
                    name="end_time"
                    class="booking-end-time"
                    :rules="endTimeRules"
                    :amPmMode="amPmMode"
                    v-model="endTime"
                  >
                  </HourPicker>
                </v-layout>
              </v-layout>

              <v-layout class="warning-item" align-content-start
                        v-if="shouldDisplayTimeInPastWarning">
                <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
                <div class="warning-text">{{$t('common.errors.time_in_past')}}</div>
              </v-layout>

              <v-layout class="warning-item" align-content-start
                        v-if="shouldDisplayEndFollowingDayWarning">
                <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
                <div class="warning-text">{{$t('settings.service_page.availability.errors.ends_following_day')}}</div>
              </v-layout>
              <StaffSelection
                data-qa="booking-staff-selection"
                v-model="selectedStaff"
                v-if="staffs && shouldDisplayStaffSelect && selectedStaff"
                :staffs="staffs"
                :additional-staffs="additionalStaffs"
                :additional-staff-availability="additionalStaffsAvailabilityWarning"
                :currentStaffUid="account.staff.uid"
                :rules="[val => !!val]"
                @submitAdditionalStaff="handleAdditionalStaff"/>
              <v-layout class="warning-item" align-content-start
                        v-if="showAvailabilityWarning">
                <v-icon size="16" color="#ffc511" class="warning-icon">icon-Warning-fill</v-icon>
                <div class="warning-text" v-html="doubleBookingWarning"/>
              </v-layout>

            </v-layout>

          </v-layout>
          <RecurrenceSelector @formatted-input="val => formattedRecurrenceParams = val"
                              :start_time="startTimeDateObject" v-model="recurrenceParams"
                              :duration="getDuration()"
                              v-if="!$vuetify.breakpoint.mdAndUp"/>
          <ExpansionPanels
            ref="interactionDetailsPanel"
            class="dialog-expansion-panel"
            @click-expansion="handleExpansionClick"
            :disabled="!selectedService"
            :items="[{
                  name: $t('appointment.info.location_and_price')
                }]"
          >
            <template slot="content" slot-scope="contentSlot">
              <v-layout column class="input-section">

                <LocationSelector
                  data-qa="location-selector"
                  :rules="locationRules"
                  class="location-selector"
                  @input="handleLocationSelect"
                  :value="newMeeting.interaction_type"
                  ref="locationSelector"
                />

                <div v-if="shouldDisplayZoomWarning">
                  <VcitaAlert v-if="isSelectedStaffAccountStaff"
                              :type="'attention'"
                              :header-text="'booking.info.interaction_types.zoom.authorize_to_host_title'"
                              :body-text="'booking.info.interaction_types.zoom.taken_to_zoom'"
                              :button-function="handleZoomIntegration"
                              :button-text="'booking.info.interaction_types.zoom.authorize_to_host_connect'"
                  />
                  <VcitaAlert v-else
                              :type="'attention'"
                              :header-text="'booking.info.interaction_types.zoom.staff_not_connected_title'"
                              :body-text="$t('booking.info.interaction_types.zoom.staff_needs_to_connect', {staff_name: selectedStaff.full_name})"
                              :link="'https://support.vcita.com/hc/en-us/articles/360045483013#slide-3'"
                              :link-text="'booking.info.interaction_types.zoom.here_how'"
                  />
                </div>
                <InteractionDetailsInput
                  v-if="newMeeting.interaction_type && newMeeting.interaction_type !== 'zoom'"
                  :interactionType="newMeeting.interaction_type"
                  v-model="newMeeting.interaction_details"
                  data-qa="interaction-details-input"
                  class="interaction-details-input"
                  :rules="interactionDetailsRules"
                  :defaultCountry="account.business.country_alpha2"
                />
                <FeeTypeGenerator
                :charge_type="newMeeting.charge_type"
                :service_charge_type="selectedService ? selectedService.charge_type : null"
                :price="newMeeting.price"
                @price-input="val => newMeeting.price = val"
                @charge-type-input="val => newMeeting.charge_type = val"
                :currencySymbol="newMeetingCurrencySymbol"
                :priceRules="priceRules"
                :entityType="'appointment'"
                >

                </FeeTypeGenerator>
              </v-layout>

            </template>

          </ExpansionPanels>
          <VcitaCheckbox
            v-if="!$vuetify.breakpoint.mdAndUp && !hasRedeemPackageFeature"
            :class="{hide: !canRedeemPackage}"
            :ripple="false"
            :readonly="recurrenceParams.is_recurring"
            color="#54d28c"
            data-qa="redeem-package-checkbox"
            class="dialog-checkbox"
            v-model="redeemPackageSelected"
          >
            <template v-slot:label>
              <v-tooltip
                :top="true"
                :disabled="!recurrenceParams.is_recurring"
              >
                <template v-slot:activator="{ on, attrs }">
                <span
                  v-on="on"
                  v-bind="attrs"
                >
                  {{$t('appointment.action.new.new_dialog.redeem_package')}}
                </span>

                </template>
                {{$t('appointment.action.new.new_dialog.redeem_package_disabled')}}
              </v-tooltip>
            </template>
          </VcitaCheckbox>

          <v-layout class="dialog-checkbox">
            <VcitaCheckbox
              v-if="hasRedeemPackageFeature"
              :class="{hide: !hasExistPackage || !isChargeTypePaid}"
              :ripple="false"
              color="#54d28c"
              data-qa="auto-redeem-package"
              v-model="autoRedeemPackageSelected"
            >
              <template v-slot:label>
                <span>
                  {{$t(recurrenceParams.is_recurring ? 'appointment.action.new.new_dialog.auto_redeem_package_series' : 'appointment.action.new.new_dialog.auto_redeem_package')}}

                   <VcitaInfo
                     v-if="hasRedeemPackageFeature && hasExistPackage && isChargeTypePaid"
                     :header="$t('appointment.action.new.new_dialog.auto_redeem_tooltip_header')"
                     :body="recurrenceParams.is_recurring ? $t('appointment.action.new.new_dialog.auto_redeem_tooltip_series') : $t('appointment.action.new.new_dialog.auto_redeem_tooltip')"
                     :nudge-left="50"
                     top
                   />
                </span>
              </template>
            </VcitaCheckbox>
          </v-layout>

          <VcitaCheckbox
            :ripple="false"
            :readonly="!$hasFeature('online_scheduling') || recurrenceParams.is_recurring || !!additionalStaffs.length"
            color="#54d28c"
            class="dialog-checkbox"
            data-qa="require-confirmation-checkbox"
            v-model="newMeeting.require_confirmation"
          >
            <template v-slot:label>
              <v-tooltip
                :top="true"
                :disabled="$hasFeature('online_scheduling') && !recurrenceParams.is_recurring && !additionalStaffs.length"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-on="on"
                    v-bind="attrs"
                  >
                    {{ isRequireToPayService ? $t('appointment.action.new.new_dialog.request_client_approval_and_payment') : $t('appointment.action.new.new_dialog.request_client_approval')}}
                  </span>
                </template>
                {{getTentativeCheckboxTooltip}}
              </v-tooltip>
            </template>
          </VcitaCheckbox>

          <VcitaCheckbox
            :ripple="false"
            color="#54d28c"
            class="dialog-checkbox"
            data-qa="note-checkbox"
            @click="newMeeting.notes = ''"
            v-model="shouldDisplayNoteInput">
            <template v-slot:label>
              <span>
                {{$t('appointment.action.new.new_dialog.add_note')}}
              </span>
            </template>
          </VcitaCheckbox>

          <v-textarea
            :hide-details="'auto'"

            class="dialog-text-area"
            :class="{
              hide: !shouldDisplayNoteInput,
            }"
            solo
            v-model="newMeeting.notes"
            :label="$t('appointment.action.new.new_dialog.enter_your_messages')"/>

        </v-layout>
      </v-form>
    </template>
    <template v-slot:right-side-pane>
      <v-layout class="right-side-container" column justify-start>
        <v-layout justify-start align-center class="input-section-title matter-term-title">
          {{ $t('booking.new_booking_dialog.matter_and_client_info', {matter_term: $t(matterTerm)}) }}
        </v-layout>
        <AppointmentMatterClientInfo
          class="matter-client-info"
          :assignedStaff="clientAssignedStaff"
          :serviceCreditsLeft="serviceCreditsLeft"
          :client="client">
          AppointmentMatterClientInfo
        </AppointmentMatterClientInfo>
        <VcitaCheckbox
          v-if="$vuetify.breakpoint.mdAndUp && !hasRedeemPackageFeature"
          :class="{hide: !canRedeemPackage}"
          :ripple="false"
          :readonly="recurrenceParams.is_recurring"
          color="#54d28c"
          class="dialog-checkbox"
          data-qa="redeem-package-checkbox"
          v-model="redeemPackageSelected"
          >
          <template v-slot:label>
            <v-tooltip
            :top="true"
            :disabled="!recurrenceParams.is_recurring"
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-on="on"
                  v-bind="attrs"
                >
                  {{$t('appointment.action.new.new_dialog.redeem_package')}}
                </span>

              </template>

              {{$t('appointment.action.new.new_dialog.redeem_package_disabled')}}
            </v-tooltip>
          </template>
        </VcitaCheckbox>

      </v-layout>
    </template>
  </GenericDialogLayout>
</template>

<script>
  import { bookingMixin } from '../bookingMixin.js'
  import VcitaInfo from "@/modules/common/components/VcitaInfo/VcitaInfo";
  import AppointmentMatterClientInfo from "@/modules/bookings/components/NewBookingDialog/AppointmentMatterClientInfo/AppointmentMatterClientInfo.vue"
  import servicesService from "@/modules/common/services/servicesService.js"
  import {createDateAsUTC, convertTime24To12} from "@/modules/common/services/timeService.js"
  import {createAppointment} from "@/modules/bookings/services/bookingsService.js"
  import {showSuccess, showMessage} from "@/modules/common/services/frontageToastService.js"
  import packagesService from "@/modules/packages/services/packagesService.js"
  import {mapActions, mapGetters} from "vuex";
  import moment from "moment";


  export default {
    name: 'appointmentDialog',
    mixins: [bookingMixin],
    watch: {
      canRedeemPackage: {
        immediate: true,
        async handler(newVal, oldVal) {
          this.redeemPackageSelected = !this.recurrenceParams.is_recurring && newVal;
          this.autoRedeemPackageSelected = this.hasRedeemPackageFeature && this.isChargeTypePaid && newVal;
        },
      },
      hasExistPackage: {
        immediate: true,
        async handler(newVal, oldVal) {
          this.redeemPackageSelected = !this.recurrenceParams.is_recurring && newVal;
          this.autoRedeemPackageSelected = this.hasRedeemPackageFeature && this.isChargeTypePaid && newVal;
        },
      },
      isChargeTypePaid: {
        immediate: true,
        async handler(newVal, oldVal) {
          this.autoRedeemPackageSelected = this.hasRedeemPackageFeature && this.isChargeTypePaid && newVal;
        },
      }
    },
    data() {
      return {
        isAllDay: this.isMoreThenADay(),
        autoRedeemPackageSelected: false,
      }
    },
    components:  {
      AppointmentMatterClientInfo,
      VcitaInfo,

    },
    props: {
      client: {
        type: Object,
        default: null,
      },

      analytics_properties: {
        type: Object,
        required: true,
      }
    },
    async created() {
      this.$track('New Appointment', Object.assign({}, this.analytics_properties, {new_dialog: true}))

      this.loadBusinessPackages()
      this.loadPackages({
        client_uid: this.client.uid,
        matter_uid: this.client.matter_uid,
      })
      const {startDate, endDate, startTime, endTime} = this.getPresetTimes()
      this.startDate = startDate
      this.endDate = endDate
      this.startTime = startTime
      this.endTime = endTime
      //Custom service
      if (this.meetingToDuplicate && !this.meetingToDuplicate.service_uid) {
        this.meetingToDuplicate.service_uid = 'custom'
      }
      this.initializeSelectedService()
      const unwatchCategories = this.$watch('categories', () => {
        const preselectedServiceUid = this.service_uid || (this.meetingToDuplicate && this.meetingToDuplicate.service_uid)
        if(preselectedServiceUid) {
          this.selectedService = this.servicesMap[preselectedServiceUid]
        }
        if(this.modifiedCategories.length === 1 && this.modifiedCategories[0].services.length === 1) {
          this.selectedService = this.modifiedCategories[0].services[0]
        }
        unwatchCategories()
      })

      this.setSelectedStaff()

      let pastIsoDate = new Date()
      pastIsoDate.setMonth(pastIsoDate.getMonth() - 3)
      try {
        const res = await servicesService.getServices( {
          "group_by": "pivot_service_id",
          "initiated_by_staff": true,
          "from_created_date": pastIsoDate.toISOString(),
          "sort" : "created:desc",
          "fields": "service_id",
          "per_page": 20,
          "page": 1,
          "business_id": this.account.business.uid,
        });
        this.recentlyPickedServices = _.uniqBy(res.data.appointments, 'service_id').filter(service => !!service.service_id).slice(0,3)
      } catch (e) {
        console.log(e)
      }
    },
    mounted() {
      if(!this.staffUid) {
        this.selectedStaff = this.staff
      }
      this.$nextTick(() => {
        this.didMount = true
      })
    },
    methods: {
      ...mapActions("packagesModule", ["loadBusinessPackages"]),
      isMoreThenADay() {
        const date1 = this.presetTimes.startTime;
        const date2 = this.presetTimes.endTime;
        const durationMS = Math.abs(date2 - date1);
        const durationHours = Math.ceil(durationMS / (1000 * 60 * 60));
        // In case of day or week view we open the all day for >= 24 hrs
        if (this.presetTimes.view === 'day' || this.presetTimes.view === 'week') {
          return durationHours >= 24
        }
        // In case of month view we open the all day for > 24 hrs
        return durationHours > 24
      },
      handleExpansionClick() {
        this.$refs.interactionDetailsPanel.$el.scrollIntoView()
        this.$track('New appointment - Expand location & Fee')
      },
      async handleAllDay(val) {
        this.isAllDay = val
        this.endDate = this.startDate
        if (val) {
          this.updateEndDateByDuration(1)
          this.startTime = '00:00'
          await this.$nextTick()
          this.endTime = '00:00'
          if (this.amPmMode) {
            this.startTime = convertTime24To12(this.startTime)
            this.endTime = convertTime24To12(this.endTime)
          }
        }
      },
      updateNewMeetingWithServiceDefaults() {
        const selectedService = this.selectedService

        if (!selectedService) {
          return;
        }
        this.newMeeting.interaction_type = selectedService.interaction_type || this.newMeeting.interaction_type
        const account = this.account
        const isCustom = this.isSelectedServiceCustom
        let serviceSpecificFields;
        let settingsSourceObject = isCustom ? account.business : selectedService
        serviceSpecificFields = {
          charge_type: isCustom ? 'free' : selectedService.charge_type,
          price: isCustom ? 0 : selectedService.price,
          currency: isCustom ? account.settings.currency : selectedService.currency,
          approval_mode: isCustom ? account.settings.approval_mode: selectedService.approval_mode,
          min_hours_before_meeting: settingsSourceObject.min_hours_before_meeting,
          future_bookings_limit: settingsSourceObject.future_bookings_limit,
          remind_before_in_hours_1: settingsSourceObject.remind_before_in_hours_1,
          remind_before_in_hours_2: settingsSourceObject.remind_before_in_hours_2,
          meeting_reminder_sms_1: settingsSourceObject.meeting_reminder_sms_1,
          meeting_reminder_sms_2: settingsSourceObject.meeting_reminder_sms_2,
          sms_booking_confirmation: settingsSourceObject.sms_booking_confirmation,
          auto_follow_up_hours: settingsSourceObject.auto_follow_up_hours,
        }
        //In case of a duplicate a custom appointment
        this.getSpecificDuplicateFields(serviceSpecificFields)

        this.newMeeting = {...this.newMeeting, ...serviceSpecificFields}
      },
      getDefaultBooking() {
         return {
          conversation_uid: this.conversation_uid,
          padding: 0,
          client: this.client,
          require_confirmation: false,
          notes: this.notes,
          type: 'appointment'
        }
      },
      focusOnFirstElementWithError() {
        if(this.selectedService && ((this.isPaidMeeting && !this.newMeeting.price) || !this.newMeeting.interaction_details)) {
          this.$refs.interactionDetailsPanel.expandAll()
        }
        const elementWithError = this.$refs.appointmentForm.inputs.find(input => !input.valid)
        elementWithError.$el.scrollIntoView()
        const inputElement = elementWithError.$el.querySelector('input')
        if(inputElement) {
          setTimeout(() => inputElement.focus({preventScroll:false}))
        } else {
          setTimeout(() => elementWithError.focus())
        }
      },
      async handleSubmit() {
        if(this.isSubmitting) {
          return
        }
        const isValid = this.$refs.appointmentForm.validate()

        if(!isValid) {
          this.focusOnFirstElementWithError()
          return;
        }

        if(this.shouldDisplayZoomWarning) {
          this.$refs.locationSelector.$el.scrollIntoView()
          setTimeout(() => this.$refs.locationSelector.$el.focus({preventScroll:false}))
          return;
        }

        let res;
        try {
          let callParams = this.callParams
          this.isSubmitting = true
          res = await createAppointment(callParams)
          if(this.redeemPackageSelected && !this.hasRedeemPackageFeature) {
            packagesService.redeemPackage(res.data.payment_status && res.data.payment_status.uid, this.client.uid, this.client.matter_uid).catch(() => {
              showMessage(this.$t('appointment.action.new.redeem_package_failed'))
            })
          }
        } catch (e) {
          console.error(e)
          const serverErrors = e && e.errors && e.errors.errors
          if(serverErrors) {
            this.serverErrors = serverErrors
          }
          this.isSubmitting = false
          return
        }
        this.isSubmitting = false
        const newAppointment = res.data
        let success_message = '';
        if (new Date(newAppointment.start_time) > new Date()) {
          if (newAppointment.state == 'scheduled') {
            success_message = this.$t("appointment.action.new.meeting_success.scheduled")
          } else {
            success_message = this.$t("appointment.action.new.meeting_success.invitation_sent", {with: this.client.fullcreated_name})
          }
          showSuccess(success_message, {})
        }
        this.$track('New Appointment - Success', {
          new_dialog: true,
          multi_staff_enabled: this.enableMultiStaff ? 'yes' : 'no',
          additional_staff: this.additionalStaffs.length,
          user_role: this.account.staff.role,
          multi_staff: this.staffs.length > 1 ? 'yes' : 'no'
        })
        this.$emit('submit', newAppointment)
      },
      /**
       * In case of a month view that is not a all day appointment set the start time to the next round hour
       */
      setPresetTime() {
        if (!this.isAllDay && this.presetTimes.view === 'month') {
          const now = new Date()
          now.setHours(now.getHours() + 1, 0, 0)
          this.presetTimes.startTime.setHours(now.getHours(), 0, 0)
        }
      }
    },
    computed: {
      ...mapGetters("packagesModule", ["businessPackages"]),
      clientAssignedStaff() {
        return this.staffs.find(staff => staff.uid === this.client.primary_staff_uid)
      },
      canRedeemPackage() {
        if (!this.$hasFeature("booking_packages") || !(this.selectedService && this.selectedService.id) ) {
          return false
        }
        return packagesService.isServiceInPackage(this.selectedService.id, this.packages)
      },
      hasExistPackage() {
        if (!this.$hasFeature("booking_packages") || !(this.selectedService && this.selectedService.id) ) {
          return false
        }
        return packagesService.isServiceInPackages(this.selectedService.id, this.businessPackages, false)
      },
      isChargeTypePaid() {
        let res;

        switch (this.newMeeting.charge_type){
          case "paid_non_secured":
          case "paid_force":
          case "paid":
            res = true;
            break;
          default:
            res = false;
            break;
        }

        return res;
      },
      hasRedeemPackageFeature() {
        return this.$hasFeature("auto_redeem_package")
      },
      packageContainingSelectedService() {
        if(!this.canRedeemPackage) {
          return null
        }
        return packagesService.getPackageContainingService(this.selectedService && this.selectedService.id, this.packages)
      },
      isRequireToPayService() {
        return this.selectedService && this.newMeeting.charge_type === "paid_force"
      },
      serviceCreditsLeft() {
        if(!this.canRedeemPackage) {
          return 0
        }
        const currPackage = this.packageContainingSelectedService
        const service_uid = this.selectedService.id
        const res = currPackage.booking_credits.find(credit => {
          if(credit.bookings_usage === credit.total_bookings) {
            return false
          }
          return credit.credit_items.some(service => service.item_id === service_uid)
        })
        return res.total_bookings - res.bookings_usage
      },
      callParams() {
        let start_time = null
        let end_time = null
        if (this.isStartTimeValid) {
          start_time = createDateAsUTC(this.startTimeDateObject).toISOString()
        }

        if (!isNaN(this.endTimeDateObject.getTime())) {
          const endTimeDateObject = new Date(this.endTimeDateObject)

          if (this.shouldDisplayEndFollowingDayWarning) {
            endTimeDateObject.setDate(endTimeDateObject.getDate() + 1)
          }

          //Validate that the appointment won't be more then a day
          if (!this.isAllDay) {
            endTimeDateObject.setFullYear(this.startTimeDateObject.getFullYear(), this.startTimeDateObject.getMonth(), this.startTimeDateObject.getDate())
            if (this.startTimeDateObject > endTimeDateObject) {
              endTimeDateObject.setDate(this.startTimeDateObject.getDate() + 1)
            }
          }
          end_time = createDateAsUTC(endTimeDateObject).toISOString()
        }

        const selectedService = this.selectedService
        const newAppointment = this.newMeeting
        return {
          client_id:                this.client.uid,
          conversation_id:          newAppointment.conversation_uid,
          title:                    this.isSelectedServiceCustom ? this.customTitle : selectedService.name,
          start_time:               start_time,
          end_time:                 end_time,
          padding:                  newAppointment.padding,
          approval_mode:            newAppointment.approval_mode,
          min_hours_before_meeting: newAppointment.min_hours_before_meeting,
          future_bookings_limit:    newAppointment.future_bookings_limit,
          remind_before_in_hours_1: newAppointment.remind_before_in_hours_1,
          remind_before_in_hours_2: newAppointment.remind_before_in_hours_2,
          meeting_reminder_sms_1:   newAppointment.meeting_reminder_sms_1,
          meeting_reminder_sms_2:   newAppointment.meeting_reminder_sms_2,
          sms_booking_confirmation: newAppointment.sms_booking_confirmation,
          auto_follow_up_hours:     newAppointment.auto_follow_up_hours,
          interaction_type:         newAppointment.interaction_type,
          interaction_details:      newAppointment.interaction_details,
          charge_type:              newAppointment.charge_type,
          price:                    this.isPaidMeeting ? newAppointment.price : null,
          currency:                 newAppointment.currency,
          agenda:                   newAppointment.notes,
          require_confirmation:     newAppointment.require_confirmation,
          service_id:               selectedService.id !== 'custom' ? selectedService.id : null,
          staff_id:                 this.selectedStaff.uid,
          additional_staffs:        this.additionalStaffs.join(', '),
          mobile:                   this.isMobile,
          source_type:              'initiated_by_staff',
          matter_uid:               this.client.matter_uid,
          auto_redeem:              this.autoRedeemPackageSelected,
          ...this.formattedRecurrenceParams,
        }
      },
      modifiedCategories() {
        if(!this.categories.length) {
          return []
        }
        let res = this.categories.map(category => {
          let newCategory = {...category}
          newCategory.services = newCategory.services.filter(service => service.type === "AppointmentService" || service.service_type === "appointment")
          newCategory.num_of_services = newCategory.services.length
          return newCategory
        })

        if(this.recentlyPickedServices && this.recentlyPickedServices.length && this.categories.length) {
          const categoryToAdd = {
            id: 'recentlyAdded',
            name: this.$t('booking.info.recently_picked'),
            num_of_services: this.recentlyPickedServices.length,
            position: 0,
            services: this.recentlyPickedServices.map(serviceUID => this.servicesMap[serviceUID.service_id]),
            uid: '',
          }
          res.unshift(categoryToAdd)
        }
        return res.filter(category => category.services.length > 0)
      },
      priceRules() {
        return [
          val => {
            if(this.isPaidMeeting) {
              return !!val || this.$t('booking.validation_errors.required_input')
            } else {
              return true
            }
          }
        ]
      },
      matterTerm(){
        return (this.$store.getters && this.$store.getters['accountModule/businessTerm'] && this.$store.getters['accountModule/businessTerm'].capitalized_singular) || 'settings.business_page.business_terminology.terms.clients.capitalized_singular';
      },
      getTentativeCheckboxTooltip() {
        if (!this.$hasFeature('online_scheduling')) {
          return this.$t('booking.validation_errors.ff_not_active')
        }
        if (this.recurrenceParams.is_recurring) {
          return this.$t('appointment.action.new.new_dialog.request_client_approval_disabled')
        }
        if (this.additionalStaffs.length) {
          return this.$t('appointment.action.new.new_dialog.multi_staff_request_client_approval_disabled')
        }
      }
    },
  }

</script>

<style lang="scss" scoped>
  @import '@/assets/scss/abstract/_i18n-mixins';
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";
  .booking-dialog-container {

    overflow-x: hidden;
    margin: auto;
    height: auto;
    max-height: 100%;
    min-height: 100%;
    border-radius: 0;
    @include md-and-up {
      max-width: 816px;
      max-height: 93vh;
      height: revert;
      min-height: revert;
      border-radius: 10px;
    }
    .margin-right {
      @include i-margin-mirror(0, 16px);
    }

    .spacing-gap {
      &:not(:last-child) {
        margin-bottom: 16px;
      }
      .one-flex-grow {
        flex: 1;
      }

      .booking-date-picker {
        min-width: 100%;
        margin-bottom: 16px;
        @include md-and-up {
          margin-bottom: 0;
          min-width: unset;
          flex: 1 0 162px;
          @include i-margin-mirror(0, 16px);
        }
      }

      &:not(.column) > div:not(:last-child) {
        @include i-margin-mirror(0, 16px);
      }

      &.column {
        & > div:not(:last-child) {
          margin-bottom: 16px;
        }
      }
    }

    .recurring-link {
      padding: 12px 0;
      cursor: pointer;
      font-style: normal !important;
      font-weight: 500 !important;
      font-size: 15px !important;
      color: #478FFF !important;
      @include md-and-up {
        padding: 0;
        font-size: 13px !important;
      }
      i {
        color: #478FFF;
      }
      .text {
        text-decoration-line: underline;
      }
    }
    .dialog-expansion-panel {
      margin-bottom: 16px;

      ::v-deep {
        .default-header {
          width: unset;
        }
      }
    }
    .dialog-text-area {
      overflow: hidden;
      max-height: 1000px;
      transition: max-height 0.3s;

      font-family: var(--primary-font-family);
      font-style: normal;
      font-weight: normal;
      font-size: 15px;
      line-height: 24px;
      letter-spacing: -0.2px;
      color: rgba(0, 0, 0, 0.4);

      ::v-deep {
        .v-input__slot {
          height: 104px;
          box-shadow: none !important;
          border: 1px solid rgba(0, 0, 0, 0.12);
        }
        textarea {
          line-height: 24px;
        }
      }
    }
    ::v-deep {
      .v-expansion-panel--active {
        @include md-and-up {
          margin-bottom: 0;
        }
      }
      .v-expansion-panel--disabled {
        .v-expansion-panel-header__icon {
          display: revert !important;
        }
        button {
          font-size: 14px;
          font-weight: 600;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.43;
          letter-spacing: normal;
          color: rgba(0, 0, 0, 0.4);
          i {
            color: rgba(0, 0, 0, 0.4) !important;
          }
        }
      }
      .v-expansion-panel-header {
        padding: 0 !important;
      }
      .v-expansion-panel-header__icon {
        order: 0;
      }
      .panel-title {
        order: 1;
        margin: 0 8px;
      }
      .v-expansion-panel {
        padding: 0;
        &:before {
          box-shadow: none;
        }
        .v-expansion-panel-content__wrap {
          padding: 0;
        }
        .v-expansion-panel-header {
          .v-icon {
            position: initial;
            top: unset;
            @include i-position-mirror(left, unset);
          }
        }
      }
  }
  .custom-title-input {
    overflow: hidden;
  }
  .hide {
    max-height: 0;
  }
}
.dialog-form {
  max-width: 100%;
  min-width: 100%;
  @include hide-scrollbar;
  @include md-and-up {
    max-height: 72vh;
    overflow-y: auto;
  }
}
.switch-button {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
  text-decoration-line: underline;
  @include i-margin-mirror(0, 10px);
}
.booking-details-section {

  .service-select {
    flex: 0;
    margin-bottom: 20px;
  }

  .hour-pickers {
    max-height: 88px;
  }

  .even-date-picker {
    .v-input__slot {
      background-color: red;
      border-radius: 0;
    }
    & .v-input__slot, fieldset {
      @include i-border-left-radius(6px);
      @include i-border-width-right-mirror(0, 1px);
    }
  }
}

.input-section {
  flex: 0;
  padding: 16px;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.03);
  & > div:not(:last-child) {
    margin-bottom: 16px;
  }
}
.warning-text {
  font-family: var(--primary-font-family);
  align-self: center;
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.2px;
}
.left-side-container {
  & > div:last-child {
    margin-bottom: 120px;
    @include md-and-up {
      margin-bottom: 0;
    }
  }
}
.right-side-container {
  & > div {
    flex: revert;
  }
  & > div:not(:last-child) {
    margin-bottom: 16px;
  }
  @include md-and-up {
    min-width: 275px;
    max-width: 275px;
  }
}
.warning-icon {
  @include i-padding-mirror(0, 14px);
  max-height: 20px;
}

.dialog-checkbox {
  text-align: initial;
  max-height: 300px;
  transition: max-height 0.3s;
  overflow: hidden;
  &.hide {
    margin-top: -16px;
  }
  &:not(:last-child) {
    margin-bottom: 16px;
  }
}

.input-section-title {
  font-family: var(--primary-font-family);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.33;
  letter-spacing: -0.2px;
  margin-bottom: 16px;
  white-space: nowrap;
  @include md-and-up {
    margin-bottom: 8px;
  }
}
.appointment-details-title {
  @include md-and-up {
    margin-inline-end: 8px;
  }
}
.matter-term-title {
  margin-bottom: 16px;
}
.dialog-separator {
  width: 100%;
  min-height: 1px;
  background-color: rgba(0, 0, 0, 0.12);
  min-width: calc( 100% + 50px );
  margin-left: -25px;
}
.service-separator {
  margin-bottom: 16px;
}
</style>

<style lang="scss">
  @import '@/assets/scss/abstract/_i18n-mixins';
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";
  .booking-dialog-container {
    .booking-start-time {
      @include md-and-up {
        min-width: 120px;
        max-width: 120px;
      }
    }
    .booking-start-date {
      min-width: 155px;
      max-width: 155px;
      @include md-and-up {
        min-width: 205.5px;
        max-width: 205.5px;
      }
    }

    .booking-start-time, .booking-start-date{
      flex: 1;

      .v-input__slot {
        border-radius: 0;
      }
      & .v-input__slot, fieldset {
        @include i-border-left-radius(6px);
        @include i-border-width-right-mirror(0, 1px);
      }
    }
    .booking-end-time ,.booking-end-date {
      flex: 1;
      @include md-and-up {
        min-width: auto;
        max-width: auto;
      }
      .v-input__slot {
        border-radius: 0;
        border-left: 1px solid rgba(0, 0, 0, 0.12);
      }
      & .v-input__slot, fieldset {
        @include i-border-right-radius(6px);
      }
    }
    .country-code {
      .v-select__slot {
        .v-select__selections {
          .vti__flag {
            @include i-margin-mirror(20px, 0)
          }
        }
      }
    }
  }
</style>



